import React, {
  ComponentType,
  ElementType,
  ErrorInfo,
  PureComponent,
} from 'react';
import hoistStatics from 'hoist-non-react-statics';
import { memoize } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import monitor from 'lib/utils/monitor';

import ErrorBox, { ErrorBoxProps } from './ErrorBox';

type Options = {
  ErrorComponent?: ComponentType<ErrorBoxProps>;
  tags?: Record<string, string>;
};

export default function withErrorHandling(
  Child: ElementType,
  options: Options = {},
) {
  const { tags = {}, ErrorComponent = ErrorBox } = options;
  const getGuid = memoize(() => uuidv4());

  type State = { hasError: boolean };

  class ChildWithErrorHandling<T extends {}> extends PureComponent<T, State> {
    static getChildName = () =>
      Child instanceof Function ? Child.displayName || Child.name : Child;

    static displayName = `withErrorHandling(${ChildWithErrorHandling.getChildName()})`;

    state = { hasError: false };

    static getDerivedStateFromError() {
      return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
      monitor.error(error, {
        extra: errorInfo,
        tags: { from: 'ErrorHandler', guid: getGuid(), ...tags },
      });
    }

    render() {
      const { hasError } = this.state;

      return hasError ? (
        <ErrorComponent guid={getGuid()} />
      ) : (
        <Child {...this.props} />
      );
    }
  }

  return Child instanceof Function
    ? hoistStatics(ChildWithErrorHandling, Child)
    : ChildWithErrorHandling;
}
